import { Outlet, Navigate, useLocation } from 'react-router-dom'
import { useEffect } from 'react'
import { Menu } from 'lucide-react'
import { Sidebar } from './Sidebar'
import {
  CONTRACT_PATH,
  ONBOARDING_PATH,
  isOpenWithoutOnboarding,
  needsContract,
  needsOnboarding,
} from './onboardingAccess'
import { SubscriptionGate } from './SubscriptionGate'
import { NO_PLAN_PATH, isOpenWithoutPlan, needsPlan } from '@/lib/planFeatures'
import { LoadingOverlay } from '@/components/ui'
import { useAuthStore } from '@/stores/authStore'
import { useUiStore, useThemeInit } from '@/stores/uiStore'
import { useChatNotifications } from '@/hooks/useChat'
import { cn } from '@/lib/utils'
import { ScrollToTop } from '@/components/ScrollToTop'
// O alerta de video pendente (spec 05, R5). Fica aqui, e nao em cada pagina, porque a
// pergunta que ele faz nao e sobre a tela atual — e sobre o que ficou pendente em
// qualquer contexto. Escopo /app/*: nem /onboarding, nem admin, nem instrutor.
import FollowupVideoAlert from '@/components/features/followup/FollowupVideoAlert'
import { PlanExpiryNotice } from '@/features/planExpiry/PlanExpiryNotice'
import { StudentContractNotice } from '@/features/studentContract/StudentContractNotice'
import { StudentLiveNowNotice } from '@/features/liveNow/LiveNowNotices'
import { FirstAccessVideosPopup } from '@/features/firstAccess/FirstAccessVideosPopup'

export function DashboardLayout() {
  const { user, isAuthenticated, isLoading, checkAuth } = useAuthStore()
  const { sidebarCollapsed, setMobileSidebarOpen } = useUiStore()
  const { pathname } = useLocation()

  // Initialize theme
  useThemeInit()

  // Initialize chat notifications (only once at dashboard level)
  useChatNotifications()

  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  if (isLoading) {
    return <LoadingOverlay message="Carregando..." />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  // r7-25: sem plano vinculado, a tela "sem plano" — ANTES do onboarding (responder o
  // formulario sem poder usar o sistema nao serve). Configuracoes continua aberta.
  if (needsPlan(user) && !isOpenWithoutPlan(pathname)) {
    return <Navigate to={NO_PLAN_PATH} replace />
  }

  // Guarda 1 do formulario de entrada (secao 7 da spec): o aluno que ainda nao concluiu
  // nao usa o app. Admin e instrutor passam direto. Ver OnboardingGate para o caminho inverso.
  // MH13-20: Configuracoes fica isenta (com ou sem plano) — e onde o aluno recem-chegado
  // permite ou nega o WhatsApp. Sem plano, o OnboardingGate mandaria de volta para /sem-plano.
  if (needsOnboarding(user) && !isOpenWithoutOnboarding(pathname)) {
    return <Navigate to={ONBOARDING_PATH} replace />
  }

  // R12: contrato da compra ainda nao assinado — a plataforma fica fechada ate assinar.
  if (needsContract(user)) {
    return <Navigate to={CONTRACT_PATH} replace />
  }

  return (
    <div className={cn(
      'min-h-screen transition-colors duration-300',
      'bg-neutral-50 dark:bg-neutral-900'
    )}>
      <ScrollToTop />
      <Sidebar />

      {/* Mobile header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-30 h-14 bg-white dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700 flex items-center px-4">
        <button
          onClick={() => setMobileSidebarOpen(true)}
          className="p-2 -ml-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
        >
          <Menu className="h-6 w-6 text-neutral-600 dark:text-neutral-300" />
        </button>
        <img
          src="https://res.cloudinary.com/dbdotnzkl/image/upload/v1702007903/Logotipo_Medhunter_1_awfuih.png"
          alt="MedHunter"
          className="h-7 object-contain dark:brightness-0 dark:invert ml-3"
        />
      </header>

      <main
        className={cn(
          'min-h-screen transition-all duration-300 ease-out-expo',
          // Mobile: no margin, add top padding for header
          'pt-14 lg:pt-0',
          // Desktop: sidebar margin
          'lg:ml-64',
          sidebarCollapsed && 'lg:ml-20'
        )}
      >
        <div className="p-4 lg:p-8">
          {/* Dentro do padding da pagina e ANTES do Outlet: fora dele brigaria com o
              header mobile (`fixed z-30`); depois dele ficaria abaixo da dobra. */}
          {/* R10-12: aula ao vivo agora (a mais urgente: vem primeiro). */}
          <StudentLiveNowNotice />
          <FollowupVideoAlert />
          {/* R10-08: ultimos dias do acesso, com o checkout do plano pago. */}
          <PlanExpiryNotice />
          {/* R12-02: contrato do aluno com plano pago (link do Autentique). */}
          <StudentContractNotice />
          {/* MH14-04: videos obrigatorios em sequencia (depois das guardas, entao depois do contrato). */}
          <FirstAccessVideosPopup />
          {/* Guarda 3 (spec 10 R4; r7-25): quem recebeu 402 do gate vai para a tela "sem
              plano" sem ser deslogado; rota de feature fora do plano vira aviso. Envolve o Outlet em vez de virar um
              `if` aqui em cima para nao competir com as guardas 1 e 2 — o redirecionamento
              so acontece depois que autenticacao e onboarding ja deixaram passar. */}
          <SubscriptionGate>
            <Outlet />
          </SubscriptionGate>
        </div>
      </main>
    </div>
  )
}
